import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// 游戏状态枚举 - 使用状态机模式
export enum GameState {
  Idle = "IDLE", // 空闲状态
  Spinning = "SPINNING", // 旋转中
  Resulting = "RESULTING", // 显示结果
  Error = "ERROR", // 错误状态
}

// 定义转盘区域类型
export interface SpinSegment {
  id: number; // 奖项ID
  text: string; // 显示文本
  value: number; // 倍数值（赔率）
  winType: "normal" | "big" | "super" | "mega" | "goodluck"; // 奖励类型
}

// 游戏配置接口
export interface GameConfig {
  gameId: number;
  platformId: number;
  minBetAmount: number;
  maxBetAmount: number;
  defaultBetAmount: number;
}

// 定义SuperSpin游戏状态
export interface SuperSpinState {
  // 状态机状态
  gameState: GameState;

  // 游戏配置
  config: GameConfig;

  // 投注相关
  betAmount: number;
  betSteps: number[]; // 添加下注金额断点数组

  // 转盘配置
  segments: SpinSegment[];
  //  当前选中的转盘区域
  currentSegment: SpinSegment | null;

  // 游戏设置
  settings: {
    fastMode: boolean;
    autoPlay: boolean;
  };

  // 时间配置
  timing: {
    resultDelay: number;
    infiniteSpinSpeed: number;
  };

  // 游戏结果
  result: {
    winAmount: number;
    segment: SpinSegment;
  };

  // 错误信息
  error: string | null;
}

// 初始状态
const initialState: SuperSpinState = {
  gameState: GameState.Idle,

  config: {
    gameId: 10004,
    platformId: 4,
    minBetAmount: 0.2,
    maxBetAmount: 500,
    defaultBetAmount: 0.2,
  },

  betAmount: 0.2,
  betSteps: [
    0.2, 0.4, 0.6, 0.8, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 30, 40, 50, 60, 70,
    80, 90, 100, 150, 200, 250, 300, 350, 400, 450, 500,
  ],

  segments: [
    { id: 0, text: "Oops!", value: 0, winType: "goodluck" },
    { id: 1, text: "0.2X", value: 0.2, winType: "normal" },
    { id: 2, text: "0.4X", value: 0.4, winType: "normal" },
    { id: 3, text: "0.6X", value: 0.6, winType: "normal" },
    { id: 4, text: "1.2X", value: 1.2, winType: "normal" },
    { id: 5, text: "2X", value: 2, winType: "normal" },
    { id: 6, text: "3X", value: 3, winType: "normal" },
    { id: 7, text: "6X", value: 6, winType: "normal" },
    { id: 8, text: "8X", value: 8, winType: "normal" },
    { id: 9, text: "10X", value: 10, winType: "big" },
    { id: 10, text: "15X", value: 15, winType: "big" },
    { id: 11, text: "20X", value: 20, winType: "big" },
    { id: 12, text: "30X", value: 30, winType: "super" },
    { id: 13, text: "50X", value: 50, winType: "super" },
    { id: 14, text: "80X", value: 80, winType: "super" },
    { id: 15, text: "150X", value: 150, winType: "mega" },
  ],
  currentSegment: null,

  settings: {
    fastMode: false,
    autoPlay: false,
  },

  timing: {
    resultDelay: 3000,
    infiniteSpinSpeed: (360 * 1) / 1000, // 每秒5圈
  },

  result: {
    winAmount: 0,
    segment: null,
  },

  error: null,
};
// 计算下注金额的断点
export const calculateBetSteps = (minBet: number, maxBet: number): number[] => {
  const steps = [];

  // 保留最小值
  steps.push(minBet);

  // 处理小数部分 (0.2 到 1.0)
  if (minBet < 1) {
    // 从最小值开始，以0.2为步长增加到1.0
    let current = minBet;
    while (current < 1 && current < maxBet) {
      current = Math.round((current + 0.2) * 10) / 10; // 避免浮点数精度问题
      if (current <= maxBet && !steps.includes(current)) {
        steps.push(current);
      }
    }
  }

  // 1到10以1为步长
  if (maxBet > 1) {
    const start1 = Math.max(1, Math.ceil(minBet));
    for (let value = start1; value <= Math.min(10, maxBet); value += 1) {
      if (value > minBet && !steps.includes(value)) {
        steps.push(value);
      }
    }
  }

  // 10到100以10为步长
  if (maxBet > 10) {
    const start10 = Math.max(10, Math.ceil(minBet / 10) * 10);
    for (let value = start10; value <= Math.min(100, maxBet); value += 10) {
      if (value > steps[steps.length - 1] && !steps.includes(value)) {
        steps.push(value);
      }
    }
  }

  // 100到1000以100为步长
  if (maxBet > 100) {
    const start100 = Math.max(100, Math.ceil(minBet / 100) * 100);
    for (let value = start100; value <= Math.min(1000, maxBet); value += 100) {
      if (value > steps[steps.length - 1] && !steps.includes(value)) {
        steps.push(value);
      }
    }
  }

  // 1000以后以500为步长
  if (maxBet > 1000) {
    const start1000 = Math.max(1000, Math.ceil(minBet / 500) * 500);
    for (let value = start1000; value <= maxBet; value += 500) {
      if (value > steps[steps.length - 1] && !steps.includes(value)) {
        steps.push(value);
      }
    }
  }

  // 添加最大值（如果不在数组中）
  if (!steps.includes(maxBet)) {
    steps.push(maxBet);
  }

  // 排序并去重，保留一位小数
  return Array.from(new Set(steps))
    .sort((a, b) => a - b)
    .map((value) => Math.round(value * 10) / 10);
};

// 创建slice
export const superSpinSlice = createSlice({
  name: "superSpin",
  initialState,
  reducers: {
    // 设置游戏状态
    setGameState: (state, action: PayloadAction<GameState>) => {
      state.gameState = action.payload;
    },

    // 设置快速模式
    setFastMode: (state, action: PayloadAction<boolean>) => {
      state.settings.fastMode = action.payload;
      // 根据快速模式调整时间配置
      if (action.payload) {
        state.timing.resultDelay = 1000;
        state.timing.infiniteSpinSpeed = (360 * 2) / 1000;
      } else {
        state.timing.resultDelay = 2000;
        state.timing.infiniteSpinSpeed = (360 * 1.5) / 1000;
      }
    },

    // 初始化游戏配置
    initializeGame: (state, action: PayloadAction<GameConfig>) => {
      state.config = action.payload;
      state.betAmount = action.payload.defaultBetAmount;
      state.gameState = GameState.Idle;
      state.error = null;
      // 计算下注金额断点
      state.betSteps = calculateBetSteps(
        action.payload.minBetAmount,
        action.payload.maxBetAmount
      );
    },

    // 设置投注金额
    setBetAmount: (state, action: PayloadAction<number>) => {
      const amount = action.payload;
      if (
        amount >= state.config.minBetAmount &&
        amount <= state.config.maxBetAmount
      ) {
        state.betAmount = amount;
      }
    },

    // 开始旋转
    startSpin: (state) => {
      // 检查状态和余额
      if (state.gameState !== GameState.Idle) {
        return;
      }

      // 进入旋转状态
      state.gameState = GameState.Spinning;
      state.currentSegment = null;
      state.result = {
        winAmount: 0,
        segment: null,
      };
    },

    // 停止旋转并结算结果
    stopSpin: (state, action: PayloadAction<SpinSegment>) => {
      const segment = action.payload;
      state.currentSegment = segment;

      // 计算赢得金额
      const winAmount = state.betAmount * segment.value;

      // 更新结果 - 直接使用碎片对象中的 winType
      state.result = {
        winAmount,
        segment,
      };

      // 进入结果状态
      state.gameState = GameState.Resulting;
    },

    // 确认结果
    confirmResult: (state) => {
      // 重置状态到空闲
      state.gameState = GameState.Idle;
      state.currentSegment = null;
      state.result = {
        winAmount: 0,
        segment: null,
      };
    },

    // 设置自动游戏
    setAutoPlay: (state, action: PayloadAction<boolean>) => {
      state.settings.autoPlay = action.payload;
    },

    // 清除错误
    clearError: (state) => {
      state.error = null;
      // 如果是错误状态，切换回idle
      if (state.gameState === GameState.Error) {
        state.gameState = GameState.Idle;
      }
    },

    // 重置游戏
    resetGame: (state) => {
      return {
        ...initialState,
        config: state.config, // 保留配置
      };
    },

    // 增加下注金额到下一个断点
    increaseBet: (state) => {
      const { betAmount, betSteps } = state;
      // 找到当前下注金额的下一个断点
      for (let i = 0; i < betSteps.length - 1; i++) {
        if (betAmount < betSteps[i + 1]) {
          state.betAmount = betSteps[i + 1];
          return;
        }
      }
      // 如果已经是最大值，则不变
      state.betAmount = state.config.maxBetAmount;
    },

    // 减少下注金额到上一个断点
    decreaseBet: (state) => {
      const { betAmount, betSteps } = state;
      // 找到当前下注金额的上一个断点
      for (let i = betSteps.length - 1; i > 0; i--) {
        if (betAmount > betSteps[i - 1]) {
          state.betAmount = betSteps[i - 1];
          return;
        }
      }
      // 如果已经是最小值，则不变
      state.betAmount = state.config.minBetAmount;
    },
  },
});

// 导出actions
export const {
  setGameState,
  initializeGame,
  setBetAmount,
  startSpin,
  stopSpin,
  confirmResult,
  setFastMode,
  setAutoPlay,
  clearError,
  resetGame,
  increaseBet,
  decreaseBet,
} = superSpinSlice.actions;

// 导出reducer
export default superSpinSlice.reducer;
